import { type Getter, type Setter, atom } from "jotai";
import { atomFamily } from "jotai/utils";

import type { RequestSpec } from "@moment/api-collab";
import { Logger } from "@moment/logging";

import { $accessTokenFunc, $isAuthenticated } from "../authenication";
import { writeRequestPageSnapshot } from "../diagnostics-snapshot";
import { $docsApi } from "../docsapi";
import {
	$document,
	$templateDocuments,
	docsListItemLocalEntry,
	docsListItemRemoteEntry,
} from "../document-list";
import { metadataKeyForDocsRoute } from "../document-metadata/documentMetadataKey";
import { $documentMetadata } from "../document-metadata/metadata-state.atom";
import { env } from "../env";
import { $resolvedDocumentEnvironmentVars, $selectedEnvironmentName } from "../environments";
import { fs } from "../filesystem";
import { getDocumentDotMomentPath } from "../paths/paths.internal";
import { logRequestPageError } from "../runtime-error-log";
import { $lastQueryKeyForPage, pageKey } from "./page-last-status.atom";
import { $queryExecutorCore } from "./query-executor-core.atom";
import {
	type QueryBuilder,
	type QueryBuilderOptions,
	type QueryExecutorInput,
	type QueryHandle,
	createQueryBuilder as createQueryBuilderImpl,
	createQueryHandle as createQueryHandleImpl,
} from "./query-handle";
import {
	$queryState,
	type QueryStateEntry,
	classifyError,
	createEmptyQueryState,
	registerQueryStateKeyForDocument,
} from "./query-state.atom";
import { $httpRequestPage } from "./request-page.atom";
import type { QueryResponse } from "./request-page.types";
import { mergeParams } from "./request-params";
import { requestPageKeyForDocsRoute } from "./requestPageKey";
import { type MomentVars, findUnresolvedVariables } from "./substitute-variables";
import {
	type TemplateResponsesFile,
	lookupTemplateResponse,
	parseTemplateResponses,
} from "./template-responses";

// Re-export from query-handle.ts
export {
	createQueryBuilderImpl as createQueryBuilder,
	createQueryHandleImpl as createQueryHandle,
	type QueryBuilder,
	type QueryBuilderOptions,
	type QueryHandle,
};

export type { QueryExecutorInput };

const logger = new Logger("query-executor");
const fsApi = fs();

// =============================================================================
// Query Executor (Document-scoped)
// =============================================================================

/**
 * Write-only atomFamily that resolves page names to requests and delegates to core.
 *
 * Usage:
 * ```ts
 * const dispatch = useSetAtom($queryExecutor(documentId));
 * const result = await dispatch({ pageName: "listPods", key: "my-key", params: {} });
 * ```
 */
export const $queryExecutor = atomFamily((documentId: string) => {
	const executorAtom = atom(
		null, // Write-only
		async (get, set, input: QueryExecutorInput): Promise<QueryStateEntry> => {
			const { pageName, key, params, isStableKey } = input;

			// Resolve page name to request spec
			const metadataKey = metadataKeyForDocsRoute(documentId);
			const metadata = get($documentMetadata(metadataKey));

			if (!metadata) {
				const error = classifyError({ message: "Document metadata not loaded" });
				logRequestPageError({
					documentId,
					pageName,
					error: { message: error.message, code: error.code },
				});
				// No snapshot write here — pageId is unknown, so the snapshot
				// would be a no-op. The error is still captured in runtime-errors.log.
				const now = Date.now();
				const stateAtom = $queryState(key);
				const newState: QueryStateEntry = {
					...createEmptyQueryState(),
					error,
					errorUpdatedAt: now,
					lastAccessedAt: now,
				};
				set(stateAtom, newState);
				return newState;
			}

			// When pageId is provided (UI execution), use it directly.
			// Otherwise fall back to title-based lookup (code cell query() calls).
			let pageId: string;
			if (input.pageId) {
				pageId = input.pageId;
			} else {
				const foundPage = metadata.pagesMetadata.find(
					(p) => p.title === pageName && p.type === "request"
				);

				if (!foundPage) {
					const error = classifyError({
						message: `Request page "${pageName}" not found`,
					});
					logRequestPageError({
						documentId,
						pageName,
						error: { message: error.message, code: error.code },
					});
					// No snapshot write here — pageId is unknown, so the snapshot
					// would be a no-op. The error is still captured in runtime-errors.log.
					const now = Date.now();
					const stateAtom = $queryState(key);
					const newState: QueryStateEntry = {
						...createEmptyQueryState(),
						error,
						errorUpdatedAt: now,
						lastAccessedAt: now,
					};
					set(stateAtom, newState);
					return newState;
				}

				pageId = foundPage.id;
			}
			const requestPageKey = requestPageKeyForDocsRoute({ documentId, pageId });
			const requestPage = get($httpRequestPage(requestPageKey));

			if (!requestPage) {
				const error = classifyError({ message: `Request page "${pageName}" not loaded` });
				logRequestPageError({
					documentId,
					pageId,
					pageName,
					error: { message: error.message, code: error.code },
				});
				writeRequestPageSnapshot({
					documentId,
					pageId,
					pageName,
					error,
				});
				const now = Date.now();
				const stateAtom = $queryState(key);
				const newState: QueryStateEntry = {
					...createEmptyQueryState(),
					error,
					errorUpdatedAt: now,
					lastAccessedAt: now,
				};
				set(stateAtom, newState);
				return newState;
			}

			// Track this as the last query key for this page (for status badge display)
			set($lastQueryKeyForPage(pageKey(documentId, pageId)), key);

			// Track key ownership for document-scoped lookups (e.g. AWS login error detection)
			registerQueryStateKeyForDocument(get, set, documentId, key);

			const isTemplate = metadata.type === "template";
			const isTemplatePreview = isTemplate && documentId in get($templateDocuments);

			// Template preview: serve cached responses only, never execute real requests
			if (isTemplatePreview) {
				if (isStableKey) {
					const cached = await resolveTemplateResponse(get, documentId, pageId, key);
					if (cached) {
						set($queryState(key), cached);
						return cached;
					}
				}

				// If $queryState was pre-seeded (e.g. by preSeedQueryState in the www
				// template preview), preserve that data instead of overwriting it with
				// empty state. This prevents a flash where the cell briefly shows data
				// from the pre-seeded state, then goes blank when the executor resolves.
				const existing = get($queryState(key));
				if (existing.data !== undefined) {
					return existing;
				}

				const now = Date.now();
				const emptyState: QueryStateEntry = {
					...createEmptyQueryState(),
					lastAccessedAt: now,
				};
				set($queryState(key), emptyState);
				return emptyState;
			}

			// Merge spec-level params (from the Postgres Kv[] UI) with caller-supplied
			// params.  Caller params take precedence so programmatic callers can
			// override defaults defined in the request page.  For HTTP requests this
			// is a no-op (returns the caller params unchanged).
			const mergedParams = mergeParams(requestPage.request.request, params);

			// Template data fallback: when a template document has unresolved env/credential
			// vars (env.* or env._moment.*), serve cached template-responses.json data
			// instead of showing an error. Missing params.* are NOT covered — those are
			// caller bugs and should surface as errors.
			// Once the user's env fully covers all variables, requests execute live.
			let hasUnresolvedEnvVars = false;
			if (isStableKey) {
				try {
					const unresolvedVars = await checkUnresolvedVariables(
						get,
						requestPage.request.request,
						mergedParams,
						documentId
					);

					// Only fall back for env-scoped unresolved vars (env.X, env._moment.X),
					// not for missing params or unsupported syntax
					const unresolvedEnvVars = unresolvedVars.filter((v) => v.startsWith("env."));
					hasUnresolvedEnvVars = unresolvedEnvVars.length > 0;

					if (hasUnresolvedEnvVars) {
						logger.debug("Template has unresolved env variables, serving cached data", {
							documentId,
							pageId,
							key,
							unresolvedEnvVars,
						});

						// Try local disk cache first, then remote
						const cached =
							requestPage.source === "disk"
								? await resolveLocalTemplateResponse(get, documentId, pageId, key)
								: await resolveTemplateResponse(get, documentId, pageId, key);

						if (cached) {
							set($queryState(key), cached);
							return cached;
						}

						// No cached data available — fall through to core which will surface
						// the unresolved-variables error as usual
					}
				} catch (err) {
					// Environment resolution failed — fall through to core executor
					// which will surface the error through normal query state handling
					logger.debug("Unresolved-variable precheck failed, delegating to core", {
						documentId,
						pageId,
						key,
						err,
					});
				}
			}

			// SWR for local templates: return stale data immediately, revalidate in background.
			// Skip if we already checked the local cache in the unresolved-env fallback above.
			if (requestPage.source === "disk" && isStableKey && !hasUnresolvedEnvVars) {
				const stale = await resolveLocalTemplateResponse(get, documentId, pageId, key);
				if (stale) {
					set($queryState(key), stale);

					// Nonblocking revalidation in background. Awkward `.then` instead of `await`
					// because we want to return instantly.
					void set($queryExecutorCore, {
						key,
						request: requestPage.request.request,
						params: mergedParams,
						documentId,
						pageId,
						pageName,
						requestPage: requestPage.request,
					})
						.then((result) => {
							if (isTemplate && result.data) {
								void saveTemplateResponse(
									get,
									set,
									documentId,
									pageId,
									key,
									result.data
								).catch((err) => {
									logger.error("Failed to auto-save template response", {
										documentId,
										err,
									});
								});
							}
						})
						.catch((err) => {
							logger.error("Background revalidation failed", {
								documentId,
								err,
							});
						});

					return stale;
				}
			}

			// Delegate to core executor
			const result = await set($queryExecutorCore, {
				key,
				request: requestPage.request.request,
				params: mergedParams,
				documentId,
				pageId,
				pageName,
				requestPage: requestPage.request,
			});

			// Auto-save: persist successful responses for local documents
			if (isTemplate && requestPage.source === "disk" && isStableKey && result.data) {
				void saveTemplateResponse(get, set, documentId, pageId, key, result.data).catch(
					(err) => {
						logger.error("Failed to auto-save template response", { documentId, err });
					}
				);
			}

			return result;
		}
	);
	executorAtom.debugLabel = `queryExecutor/${documentId}`;
	return executorAtom;
});

// =============================================================================
// Template Response Helpers
// =============================================================================

async function resolveLocalTemplateResponse(
	get: Getter,
	documentId: string,
	pageId: string,
	key: string
): Promise<QueryStateEntry | undefined> {
	try {
		if (!env.isDesktopApp) {
			return undefined;
		}

		const dotMomentPath = await getDocumentDotMomentPath(documentId);
		const filePath = await get(fsApi.pathJoin([dotMomentPath, "template-responses.json"]));
		const fileExists = await get(fsApi.exists({ filePath }));
		if (!fileExists) {
			return undefined;
		}

		const raw = await get(fsApi.readJsonFile({ filePath }));
		const responses = parseTemplateResponses(JSON.stringify(raw));
		if (!responses) {
			return undefined;
		}

		const response = lookupTemplateResponse(responses, pageId, key);
		if (!response) {
			return undefined;
		}

		const now = Date.now();
		return {
			...createEmptyQueryState(),
			data: response,
			dataUpdatedAt: now,
			lastAccessedAt: now,
		};
	} catch (err) {
		logger.debug("Local template response lookup failed, continuing to live execution", {
			documentId,
			pageId,
			key,
			err,
		});
		return undefined;
	}
}

async function resolveTemplateResponse(
	get: Getter,
	documentId: string,
	pageId: string,
	key: string
): Promise<QueryStateEntry | undefined> {
	try {
		const doc = await get($document(documentId));
		if (!doc) {
			return undefined;
		}

		const remoteEntry = docsListItemRemoteEntry(doc);
		const workspaceName = remoteEntry?.workspace.name;
		if (!workspaceName) {
			return undefined;
		}

		const { getAccessTokenSilently } = get($accessTokenFunc);
		const token = getAccessTokenSilently ? await getAccessTokenSilently() : undefined;

		const json = await get($docsApi).document.readRawFile(
			{
				workspaceName,
				documentId,
				filePath: ".moment/template-responses.json",
			},
			token
		);

		const responses = parseTemplateResponses(json);
		if (!responses) {
			return undefined;
		}

		const response = lookupTemplateResponse(responses, pageId, key);
		if (!response) {
			return undefined;
		}

		const now = Date.now();
		return {
			...createEmptyQueryState(),
			data: response,
			dataUpdatedAt: now,
			lastAccessedAt: now,
		};
	} catch (err) {
		logger.debug("Template response lookup failed, falling through to normal execution", {
			documentId,
			pageId,
			key,
			err,
		});
		return undefined;
	}
}

/**
 * Check whether a request has unresolved variables given the current environment.
 * Mirrors the resolution logic in query-executor-core.atom.ts but without executing.
 */
async function checkUnresolvedVariables(
	get: Getter,
	request: RequestSpec,
	params: Record<string, string>,
	documentId: string
): Promise<string[]> {
	const environmentName = await get($selectedEnvironmentName);
	const envVars = await get($resolvedDocumentEnvironmentVars({ documentId, environmentName }));

	let momentVars: MomentVars = {};
	const isAuthenticated = get($isAuthenticated);
	if (isAuthenticated) {
		const tokenFunc = get($accessTokenFunc);
		if (tokenFunc?.getAccessTokenSilently) {
			try {
				const token = await tokenFunc.getAccessTokenSilently();
				momentVars = { auth_token: token };
			} catch {
				// Token fetch failed, leave momentVars empty
			}
		}
	}

	return findUnresolvedVariables(request, envVars, params, momentVars);
}

// Per-document write queue to serialize saves to template-responses.json. Without this, concurrent
// saves (common on first load) each read the file independently, add their own entry, and write
// back — only the last writer's entry survives.
const saveQueues = new Map<string, Promise<void>>();

async function saveTemplateResponse(
	get: Getter,
	set: Setter,
	documentId: string,
	pageId: string,
	key: string,
	response: QueryResponse
): Promise<void> {
	const previous = saveQueues.get(documentId) ?? Promise.resolve();
	const next = previous
		.then(() => saveTemplateResponseImpl(get, set, documentId, pageId, key, response))
		.catch((err) => {
			logger.error("Failed to save template response", { documentId, err });
		});
	saveQueues.set(documentId, next);
	await next;
}

async function saveTemplateResponseImpl(
	get: Getter,
	set: Setter,
	documentId: string,
	pageId: string,
	key: string,
	response: QueryResponse
): Promise<void> {
	if (!env.isDesktopApp) {
		return;
	}

	const doc = await get($document(documentId));
	if (!doc) {
		return;
	}

	const localEntry = docsListItemLocalEntry(doc);
	if (!localEntry?.path) {
		return;
	}

	const dotMomentPath = await getDocumentDotMomentPath(documentId);
	const filePath = await get(fsApi.pathJoin([dotMomentPath, "template-responses.json"]));

	let existing: TemplateResponsesFile = {};
	try {
		const fileExists = await get(fsApi.exists({ filePath }));
		if (fileExists) {
			const text = await get(fsApi.readTextFile({ filePath }));
			const parsed = parseTemplateResponses(text);
			if (parsed) {
				existing = parsed;
			}
		}
	} catch {
		// File doesn't exist or is invalid, start fresh
	}

	if (!existing[pageId]) {
		existing[pageId] = {};
	}
	existing[pageId][key] = response;

	await set(fsApi.mkdir, { dirPath: dotMomentPath });
	await set(fsApi.writeJsonFile, { filePath, content: existing, documentId });

	logger.info("Saved template response", { documentId, pageId, key });
}
